import { ChevronLeft, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'

interface WizardFooterProps {
  /** 1-based. Junto com `totalSteps`, é o que preenche a barra de progresso. */
  step: number
  totalSteps: number
  onBack: () => void
  onNext: () => void
  nextDisabled?: boolean
  /** "Continuar" na maioria dos passos; o último manda gerar. */
  nextLabel?: string
  busy?: boolean
  /**
   * O que falta para poder avançar ("Marque pelo menos um serviço"). Aparece
   * só quando o botão está travado — botão apagado sem explicação é o mesmo
   * beco sem saída de não achar o botão.
   */
  hint?: string | null
  /**
   * O carrinho, só no celular: o total e o gesto que abre a gaveta do resumo.
   * No desktop o resumo é a coluna da direita e isto não é passado.
   */
  cart?: { total: string; caption: string; onOpen: () => void }
}

/**
 * A barra de ação do wizard de criação de página.
 *
 * FIXA no rodapé da janela, e não no fim do cartão do passo. Antes o
 * "Continuar" ficava depois da lista inteira de 7 categorias e 32 cards: quem
 * não rolava até o fim não descobria que dava para avançar — o passo parecia
 * uma tela sem saída. É o padrão de Airbnb e Booking, e pela mesma razão: num
 * fluxo de vários passos, o caminho para a frente não pode depender de rolagem.
 *
 * A barra de progresso em cima dela responde a outra pergunta que o wizard não
 * respondia: quanto ainda falta.
 *
 * O conteúdo do passo precisa reservar a altura desta barra no rodapé
 * (`WIZARD_FOOTER_SPACING`) — sem isso o último campo nasce embaixo dela.
 */
export default function WizardFooter({
  step,
  totalSteps,
  onBack,
  onNext,
  nextDisabled = false,
  nextLabel = 'Continuar',
  busy = false,
  hint,
  cart,
}: WizardFooterProps) {
  const progress = Math.min(100, Math.round((step / totalSteps) * 100))

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur',
        'shadow-[0_-4px_16px_rgba(16,24,40,0.06)]',
        // Área segura do iPhone: sem isto os botões ficam atrás da barra de
        // gestos.
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      {/* Progresso. `aria-hidden` porque a mesma informação é dita em texto ao
          lado dos botões — duas leituras da mesma coisa só atrapalham. */}
      <div aria-hidden="true" className="h-1 w-full bg-gray-100">
        <div
          className="h-full bg-primary transition-[width] duration-300 ease-out motion-reduce:transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mx-auto flex max-w-6xl items-center gap-3 px-5 py-3 sm:px-6 lg:px-10">
        <button
          type="button"
          onClick={onBack}
          disabled={step === 1 || busy}
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold text-gray-700',
            'transition-colors hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-0',
          )}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          Voltar
        </button>

        {/* O carrinho ocupa o meio no celular; no desktop o meio é só o contador
            de passos, porque o resumo já é a coluna da direita. */}
        {cart ? (
          <button
            type="button"
            onClick={cart.onOpen}
            // Rótulo explícito: sem ele o nome acessível vira "R$ 8,90 1 ×
            // Mensal", que não diz o que o botão FAZ.
            aria-label="Ver resumo do valor"
            className="flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1 text-left transition-colors hover:bg-gray-50 lg:hidden"
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold leading-tight text-gray-900">
                {cart.total}
              </span>
              <span className="block truncate text-[0.6875rem] leading-tight text-gray-500">
                {cart.caption}
              </span>
            </span>
            <ChevronUp
              className="size-4 shrink-0 text-gray-400"
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="flex-1" />
        )}

        <p
          className={cn(
            'hidden flex-1 text-center text-xs text-gray-500 lg:block',
            cart && 'lg:flex-none lg:px-2',
          )}
        >
          Passo {step} de {totalSteps}
        </p>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <button
            type="button"
            onClick={onNext}
            disabled={nextDisabled || busy}
            className={cn(
              'rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground',
              'transition-all duration-200 hover:opacity-90 active:scale-[0.98]',
              'motion-reduce:transition-none motion-reduce:active:scale-100',
              'disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100',
            )}
          >
            {busy ? 'Salvando…' : nextLabel}
          </button>
        </div>
      </div>

      {/* A dica vive numa linha própria embaixo: ao lado do botão ela espremia
          os dois num celular estreito. */}
      {hint && nextDisabled && (
        <p className="mx-auto max-w-6xl px-5 pb-2.5 text-right text-xs text-gray-500 sm:px-6 lg:px-10">
          {hint}
        </p>
      )}
    </div>
  )
}

/**
 * O espaço que o conteúdo do passo precisa deixar no rodapé para não nascer
 * atrás da barra. Constante e não `pb-28` solto: são duas medidas que precisam
 * concordar, e concordar num lugar só.
 */
export const WIZARD_FOOTER_SPACING = 'pb-32 sm:pb-28'
